
import { USE_WEBGPU } from "../wgs/globals";
import { createElement, setLocalizedText } from './DomUtils';

import i18n from "i18next";

const av = Autodesk.Viewing;
const avp = av.Private;

const FEEDBACK_EMAIL = 'viewer-experimental@autodesk.com'; // Autodesk Mail Group
const LIKERT_LABELS = ["Poor", "Fair", "OK", "Good", "Great"];

/**
 * Builds the content of the experimental tab: a disclaimer banner at the top, experiment toggle checkboxes
 * gated by WebGPU availability, and per-setting Likert rating widgets for user feedback. The panel parameter
 * provides the SettingsPanel interface needed to create checkboxes and access controls.
 *
 * @param {Object} panel - SettingsPanel interface: { addCheckbox, getControl, tabIdToIndex, tablesContainer }
 * @param {string} tabId - The tab identifier for the experimental tab.
 * @param {Array} experiments - Filtered experiment definitions for the current viewer mode.
 */
export function createExperimentalPanel(panel, tabId, experiments) {
  const table = panel.tablesContainer.childNodes[panel.tabIdToIndex[tabId]];

  const disclaimerRow = table.tBodies[0].insertRow(-1);
  const disclaimerCell = disclaimerRow.insertCell(0);
  disclaimerCell.colSpan = "3";
  disclaimerCell.classList.add("pb-3");

  const disclaimer = createElement(disclaimerCell, "div", "experimental-disclaimer");
  createElement(disclaimer, "span", "experimental-disclaimer-icon", "adsk-alert-icon-outlined", "icon-size-24");

  const body = createElement(disclaimer, "div", "experimental-disclaimer-body");
  const text = createElement(body, "span");
  setLocalizedText(text, "Experimental Disclaimer");

  body.appendChild(body.ownerDocument.createTextNode(" ")); // spacer

  const feedbackLink = createElement(body, "a", "experimental-feedback-link");
  setLocalizedText(feedbackLink, "Send Feedback");
  feedbackLink.href = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent("Viewer Experimental Feature Feedback")}`;
  feedbackLink.addEventListener("click", () => avp.analytics.track('viewer.settings.experimental_feedback_clicked'));

  for (const experiment of experiments) {
    const isActive = experiment.enabled !== false && USE_WEBGPU;

    if (experiment.type === 'dropdown-secondary') {
      const dropDownId = panel.addDropDownMenu(tabId, experiment.name,
      experiment.items, experiment.values, null, experiment.saveKey);

      const control = panel.getControl(dropDownId);
      control.setDisabled(!isActive);
      control.sliderRow.style.opacity = isActive ? 1.0 : 0.5;

      const row = control.sliderRow;
      row.classList.add("switch-slider-row");
      const dropdownCell = row.cells[1];
      dropdownCell.style.paddingRight = "16px";
      dropdownCell.style.paddingLeft = "0px";

      const descriptionCell = row.insertCell(1);
      const descriptionElement = row.ownerDocument.createElement("div");
      descriptionElement.setAttribute("data-i18n", experiment.description);
      descriptionElement.textContent = i18n.t(experiment.description);
      descriptionCell.appendChild(descriptionElement);
    } else {
      const checkboxId = panel.addCheckbox(tabId,
      experiment.name, experiment.description,
      experiment.initialState ?? false, undefined, experiment.saveKey);

      const control = panel.getControl(checkboxId);
      control.setValue?.(isActive ? control.getValue() : false);
      control.setDisabled(!isActive);
      control.sliderRow.style.opacity = isActive ? 1.0 : 0.5;
    }

    addLikertRating(table, experiment.saveKey, !isActive);
  }
}

/**
 * Inserts a compact Likert rating row (1–5) below a setting toggle. Uses the table's three-column layout:
 * column 1 = "Rate this Feature" prompt, column 2+3 = pill buttons + Submit. The user selects a pill, then
 * clicks Submit to fire the analytics event. Submit stays disabled until a pill is selected and locks
 * permanently after one successful submission per session.
 */
function addLikertRating(table, saveKey, disabled) {
  const row = table.tBodies[0].insertRow(-1);
  row.classList.add("experimental-likert-row");
  if (disabled) row.style.opacity = "0.5";

  const promptCell = row.insertCell(0);
  const prompt = createElement(promptCell, "span", "experimental-likert-prompt");
  setLocalizedText(prompt, "Rate this Feature");

  const controlsCell = row.insertCell(1);
  controlsCell.colSpan = "2";

  const controlsContainer = createElement(controlsCell, "div", "experimental-likert-controls");
  const pillsContainer = createElement(controlsContainer, "div", "experimental-likert-pills");

  let selectedRating = -1;
  let submitted = false;

  const submitButton = createElement(controlsContainer, "button", "experimental-likert-submit");
  setLocalizedText(submitButton, "Submit");
  submitButton.disabled = true;

  LIKERT_LABELS.forEach((text, index) => {
    const value = index + 1;
    const button = createElement(pillsContainer, "button", "experimental-likert-btn");
    setLocalizedText(button, text);
    button.title = `${value}/5`;
    button.disabled = disabled;

    button.addEventListener("click", () => {
      if (submitted || disabled) return;

      selectedRating = value;
      for (const btn of pillsContainer.querySelectorAll(".experimental-likert-btn")) btn.classList.remove("selected");
      button.classList.add("selected");
      submitButton.disabled = false;
    });
  });

  submitButton.addEventListener("click", () => {
    if (submitted || selectedRating < 0 || disabled) return;

    submitted = true;
    submitButton.disabled = true;
    submitButton.classList.add("submitted");
    submitButton.textContent = i18n.t("Thanks!");

    for (const btn of pillsContainer.querySelectorAll(".experimental-likert-btn")) btn.disabled = true;

    avp.analytics.track('viewer.settings.experimental_rating', {
      setting_name: saveKey,
      rating: selectedRating
    });
  });
}